import React, { useState } from 'react';
import { FlaskConical, Info } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';

// Repères de référence adulte (hors grossesse), sources : Cleveland Clinic (NFS) et
// Johns Hopkins Medicine (bilan lipidique). Un laboratoire peut publier des normes
// légèrement différentes selon sa méthode — celles imprimées sur votre bilan
// prévalent toujours.
const TESTS = {
  hemoglobine: {
    label: 'Hémoglobine',
    unit: 'g/dL',
    needsSex: true,
    range: { homme: [13, 17], femme: [11.5, 15.5] },
    convert: { 'g/dL': (v) => v, 'g/L': (v) => v / 10 },
  },
  hematocrite: {
    label: 'Hématocrite',
    unit: '%',
    needsSex: true,
    range: { homme: [40, 55], femme: [36, 48] },
    convert: { '%': (v) => v },
  },
  globules_blancs: {
    label: 'Globules blancs (leucocytes)',
    unit: '/µL',
    needsSex: false,
    range: { tous: [4000, 10000] },
    convert: { '/µL': (v) => v, 'G/L (x10⁹/L)': (v) => v * 1000 },
  },
  plaquettes: {
    label: 'Plaquettes',
    unit: '/µL',
    needsSex: false,
    range: { tous: [150000, 400000] },
    convert: { '/µL': (v) => v, 'G/L (x10⁹/L)': (v) => v * 1000 },
  },
  cholesterol_total: {
    label: 'Cholestérol total',
    unit: 'mg/dL',
    needsSex: false,
    range: { tous: [0, 200] },
    convert: { 'mg/dL': (v) => v, 'g/L': (v) => v * 100, 'mmol/L': (v) => v * 38.67 },
  },
  ldl: {
    label: 'Cholestérol LDL ("mauvais")',
    unit: 'mg/dL',
    needsSex: false,
    range: { tous: [0, 100] },
    convert: { 'mg/dL': (v) => v, 'g/L': (v) => v * 100, 'mmol/L': (v) => v * 38.67 },
    note: 'La cible optimale de LDL dépend surtout de votre risque cardiovasculaire global (100 mg/dL est le seuil optimal usuel, mais un médecin peut viser plus bas en cas de diabète ou d\'antécédents cardiovasculaires).',
  },
  hdl: {
    label: 'Cholestérol HDL ("bon")',
    unit: 'mg/dL',
    needsSex: false,
    range: { tous: [40, Infinity] },
    convert: { 'mg/dL': (v) => v, 'g/L': (v) => v * 100, 'mmol/L': (v) => v * 38.67 },
    note: 'Pour le HDL, plus haut est généralement meilleur (au-delà de 60 mg/dL, il est considéré comme protecteur).',
  },
  triglycerides: {
    label: 'Triglycérides',
    unit: 'mg/dL',
    needsSex: false,
    range: { tous: [0, 150] },
    convert: { 'mg/dL': (v) => v, 'g/L': (v) => v * 100, 'mmol/L': (v) => v * 88.5 },
  },
};

const classify = (canonicalVal, [low, high], testKey) => {
  if (testKey === 'hdl') {
    // Pour le HDL, en dessous du seuil = risque ; au-dessus = protecteur.
    if (canonicalVal < low) return { label: 'Bas (facteur de risque)', color: '#F59E0B' };
    return { label: 'Dans la norme ou protecteur', color: '#10B981' };
  }
  if (canonicalVal < low) return { label: 'Bas', color: '#F59E0B' };
  if (canonicalVal > high) return { label: 'Élevé', color: '#EF4444' };
  return { label: 'Dans la norme', color: '#10B981' };
};

const BloodPanelConverter = () => {
  const [testKey, setTestKey] = useState('cholesterol_total');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(TESTS['cholesterol_total'].unit);
  const [sex, setSex] = useState('homme');

  const test = TESTS[testKey];
  const units = Object.keys(test.convert);
  const activeUnit = units.includes(unit) ? unit : units[0];

  const v = parseFloat(value);
  const canonical = !Number.isNaN(v) && v >= 0 ? test.convert[activeUnit](v) : null;
  const range = test.needsSex ? test.range[sex] : test.range.tous;
  const result = canonical !== null ? classify(canonical, range, testKey) : null;

  const handleTestChange = (key) => {
    setTestKey(key);
    setUnit(TESTS[key].unit);
    setValue('');
  };

  const rangeLabel = range[1] === Infinity ? `≥ ${range[0]}` : `${range[0]} – ${range[1]}`;

  return (
    <ToolPageLayout
      title="Convertisseur de bilan sanguin"
      subtitle="Convertissez et repérez les valeurs usuelles de la NFS et du bilan lipidique selon l'unité de votre laboratoire."
      specialty="generaliste"
      specialtyLabel="un généraliste"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          Les normes varient légèrement d'un laboratoire à l'autre selon la méthode utilisée : fiez-vous
          toujours en priorité aux valeurs de référence imprimées sur votre compte-rendu.
        </p>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">Paramètre</Label>
        <select
          value={testKey}
          onChange={(e) => handleTestChange(e.target.value)}
          className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        >
          {Object.entries(TESTS).map(([key, t]) => (
            <option key={key} value={key}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">Valeur mesurée</Label>
          <div className="flex gap-2">
            <Input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="rounded-xl flex-1" />
            <select
              value={activeUnit}
              onChange={(e) => setUnit(e.target.value)}
              className="border border-[#E2E8F0] rounded-xl px-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
            >
              {units.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>
        {test.needsSex && (
          <div>
            <Label className="mb-1 block">Sexe</Label>
            <select
              value={sex}
              onChange={(e) => setSex(e.target.value)}
              className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
            >
              <option value="homme">Homme</option>
              <option value="femme">Femme</option>
            </select>
          </div>
        )}
      </div>

      {result && (
        <div className="rounded-2xl p-6 text-center border" style={{ borderColor: `${result.color}40`, backgroundColor: `${result.color}0D` }}>
          <FlaskConical className="w-6 h-6 mx-auto mb-2" style={{ color: result.color }} />
          <div className="text-sm text-[#64748B] mb-1">
            Valeur de référence adulte : {rangeLabel} {test.unit}
          </div>
          <div className="mt-2 font-semibold text-lg" style={{ color: result.color }}>{result.label}</div>
        </div>
      )}

      {test.note && (
        <p className="mt-4 text-sm text-[#64748B]">{test.note}</p>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        Ces repères concernent un adulte hors grossesse et en dehors de toute pathologie particulière ;
        ils ne remplacent pas l'interprétation de votre médecin, qui tient compte de l'ensemble de
        votre bilan et de votre contexte clinique.
      </p>
    </ToolPageLayout>
  );
};

export default BloodPanelConverter;
